/**
 * Mounts the maintenance page over the app while `maintenance` (see
 * ~/helpers/maintenance) says the API is down, and drives recovery polling
 * while it's showing.
 *
 * The app itself is never unmounted — just hidden. Tearing it down would
 * drop in-flight requests, timers and the React Query cache on every false
 * positive; a `<div hidden>` costs nothing while healthy (it's just a no-op
 * wrapper) and means the real app resumes exactly where it left off the
 * moment the page clears.
 */
import { ReactNode, useEffect, useSyncExternalStore } from "react";

import { maintenance, startRecovery } from "~/helpers/maintenance";
import { MaintenancePage } from "~/components/maintenance";

const apiURL: string = import.meta.env.VITE_API_URL;

/**
 * Raw fetch against the API's health check — deliberately NOT this app's
 * sendGet/axios wrapper (~/api/base.ts). That wrapper's 401 handling calls
 * forceSignOut(), which would log out every parked tab just because the app
 * happened to still be down when a recovery poll landed. Any response at all
 * proves the app server is answering again, since only it can produce a
 * CORS-valid reply.
 *
 * No `credentials` option on purpose: this must never send the auth cookie.
 * jubilee-v2-api's health_check view (core/health_check.py) is missing the
 * @permission_classes([AllowAny]) / @authentication_classes([]) decorators
 * its sibling apps' equivalents carry, and works today only because no
 * request to it carries credentials. Adding `credentials: "include"` here
 * would risk a 401 from the endpoint's default JWT-cookie authenticator.
 */
async function confirmReadiness(): Promise<boolean> {
  try {
    const res = await fetch(`${apiURL}/health_check/?cb=${Date.now()}`, {
      cache: "no-store",
      signal: AbortSignal.timeout(5000),
    });
    return res.ok;
  } catch {
    return false;
  }
}

export const MaintenanceGate = ({ children }: { children: ReactNode }) => {
  const down = useSyncExternalStore(
    maintenance.subscribe,
    maintenance.getSnapshot
  );

  useEffect(() => {
    if (!down) return;
    return startRecovery(confirmReadiness);
  }, [down]);

  return (
    <>
      {down && <MaintenancePage />}
      <div hidden={down}>{children}</div>
    </>
  );
};

export default MaintenanceGate;
